// src/pages/Admin/.../InfoClubForm.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  getSettings,
  updateSettings,
} from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaSave,
  FaMoon,
  FaSun,
  FaImage,
  FaUndo,
  FaExpandAlt,
  FaTimes,
} from "react-icons/fa";

const INFOCLUB_DEFAULT_IMAGE = "/img/infoclub.png";

/* Quill */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const InfoClubForm = () => {
  const [settings, setSettings] = useState({
    infoclub_isactive: true,
    infoclub_title: "",
    infoclub_content: "",
    infoclub_image_url: "",
    logo_club: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [previewDark, setPreviewDark] = useState(false);
  const [imageHasError, setImageHasError] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();

        if (Array.isArray(settingsData) && settingsData.length > 0) {
          const first = settingsData[0];

          setSettings((prev) => ({
            ...prev,
            ...first,
            infoclub_isactive:
              first?.infoclub_isactive === undefined ||
              first?.infoclub_isactive === null
                ? true
                : !!first.infoclub_isactive,
            infoclub_image_url: first?.infoclub_image_url || "",
          }));
        } else if (settingsData && typeof settingsData === "object") {
          setSettings((prev) => ({
            ...prev,
            ...settingsData,
            infoclub_isactive:
              settingsData?.infoclub_isactive === undefined ||
              settingsData?.infoclub_isactive === null
                ? true
                : !!settingsData.infoclub_isactive,
            infoclub_image_url: settingsData?.infoclub_image_url || "",
          }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const rawImageUrl = String(settings.infoclub_image_url || "").trim();
    setImageHasError(false);
  }, [settings.infoclub_image_url]);

  useEffect(() => {
    if (!isImageOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsImageOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isImageOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearImageUrl = () => {
    setSettings((prev) => ({
      ...prev,
      infoclub_image_url: "",
    }));

    setImageHasError(false);
  };

  const getNormalizedPayload = () => {
    return {
      ...settings,
      infoclub_isactive: !!settings.infoclub_isactive,
      infoclub_title: String(settings.infoclub_title || "").trim(),
      infoclub_content: String(settings.infoclub_content || ""),
      infoclub_image_url: String(settings.infoclub_image_url || "").trim(),
    };
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      const payload = getNormalizedPayload();

      await updateSettings(payload);

      setSettings(payload);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de la mise à jour des paramètres",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveSettings();
  };

  const imagePreviewUrl = useMemo(() => {
    if (imageHasError) return INFOCLUB_DEFAULT_IMAGE;

    return (
      String(settings.infoclub_image_url || "").trim() || INFOCLUB_DEFAULT_IMAGE
    );
  }, [settings.infoclub_image_url, imageHasError]);

  const usesDefaultImage = !String(settings.infoclub_image_url || "").trim();

  const handleImageError = (event) => {
    const img = event.currentTarget;

    if (img.dataset.fallbackApplied === "true") return;

    img.dataset.fallbackApplied = "true";
    img.src = INFOCLUB_DEFAULT_IMAGE;
    setImageHasError(true);
  };

  const previewSettings = useMemo(
    () => ({
      isActive: !!settings.infoclub_isactive,
      title: settings.infoclub_title || "Informations du club",
      content:
        settings.infoclub_content ||
        "<p>Ajoutez ici vos informations importantes : horaires, actu, rappel, etc.</p>",
      image: imagePreviewUrl,
      logo: settings.logo_club || "/img/club_logo_no-image.jpg",
    }),
    [settings, imagePreviewUrl],
  );

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="overflow-hidden rounded-2xl bg-white/70 shadow ring-1 ring-slate-200/70 backdrop-blur dark:bg-slate-900/60 dark:ring-slate-700/70">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 animate-pulse rounded bg-white/25" />
            </div>

            <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-2">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 animate-pulse rounded-xl bg-slate-100/80 dark:bg-slate-800/40"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="overflow-hidden rounded-2xl bg-white/70 shadow ring-1 ring-slate-200/70 backdrop-blur dark:bg-slate-900/60 dark:ring-slate-700/70">
            {/* HEADER */}
            <div className="flex flex-col gap-4 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Info Club — Paramètres & Prévisualisation
                </h2>

                <p className="mt-1 text-sm text-white/90 sm:text-[15px]">
                  Gérez l’encart « Info Club » de la page d’accueil avec aperçu
                  en direct.
                </p>
              </div>

              <Button
                text={saving ? "Enregistrement..." : "Enregistrer"}
                icon={FaSave}
                onClick={saveSettings}
                className="!rounded-xl !bg-white !text-slate-900 !shadow-md !hover:bg-white/90 gap-2"
              />
            </div>

            {/* CONTENT: Form + Preview */}
            <div className="grid grid-cols-1 gap-6 p-4 sm:p-6 lg:grid-cols-2">
              {/* Formulaire */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Activation */}
                <div className="rounded-xl border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40 sm:p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        Activer la section Info Club
                      </p>

                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Contrôle l’affichage sur la page d’accueil.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setSettings((prev) => ({
                          ...prev,
                          infoclub_isactive: !prev.infoclub_isactive,
                        }))
                      }
                      className={`h-7 w-14 rounded-full p-1 transition-colors ${
                        settings.infoclub_isactive
                          ? "bg-green-500"
                          : "bg-slate-300 dark:bg-slate-700"
                      }`}
                      title={
                        settings.infoclub_isactive ? "Désactiver" : "Activer"
                      }
                    >
                      <span
                        className={`block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          settings.infoclub_isactive
                            ? "translate-x-7"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Titre */}
                <div className="rounded-xl border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40 sm:p-5">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Titre de la section
                  </label>

                  <input
                    type="text"
                    name="infoclub_title"
                    value={settings.infoclub_title || ""}
                    onChange={handleChange}
                    placeholder="Ex : Informations du club"
                    className="mt-1 w-full rounded-xl border border-slate-300 bg-white/80 px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-100 dark:placeholder-slate-500"
                  />
                </div>

                {/* Image */}
                <div className="rounded-xl border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40 sm:p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <FaImage className="text-violet-500" />

                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        Image de l’encart
                      </p>

                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        URL de l’image affichée à droite du bloc Info Club.
                      </p>
                    </div>
                  </div>

                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    URL de l’image
                  </label>

                  <input
                    type="text"
                    name="infoclub_image_url"
                    value={settings.infoclub_image_url || ""}
                    onChange={handleChange}
                    placeholder="https://... ou /img/infoclub.png"
                    className="mt-1 w-full rounded-xl border border-slate-300 bg-white/80 px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-100 dark:placeholder-slate-500"
                  />

                  <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_180px] sm:items-start">
                    <div>
                      <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {usesDefaultImage
                          ? "Aucune image personnalisée : l’image par défaut /img/infoclub.png sera utilisée."
                          : imageHasError
                            ? "L’image personnalisée semble introuvable : l’image par défaut sera affichée."
                            : "Cette image sera utilisée sur la page d’accueil."}
                      </p>

                      {!usesDefaultImage ? (
                        <button
                          type="button"
                          onClick={clearImageUrl}
                          className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white/80 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-white dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FaUndo />
                          Revenir à l’image par défaut
                        </button>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsImageOpen(true)}
                      className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-left shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-950"
                      title="Agrandir l’image"
                    >
                      <div className="relative aspect-[4/3] w-full">
                        <img
                          src={imagePreviewUrl}
                          alt="Aperçu de l’image Info Club"
                          className="h-full w-full object-cover"
                          onError={handleImageError}
                        />

                        <div className="absolute inset-0 flex items-center justify-center bg-slate-950/0 transition group-hover:bg-slate-950/35">
                          <span className="inline-flex scale-95 items-center gap-2 rounded-xl bg-white/90 px-3 py-2 text-xs font-bold text-slate-900 opacity-0 shadow transition group-hover:scale-100 group-hover:opacity-100">
                            <FaExpandAlt />
                            Agrandir
                          </span>
                        </div>
                      </div>

                      <div className="border-t border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                        <p className="truncate text-xs font-bold text-slate-600 dark:text-slate-300">
                          {usesDefaultImage
                            ? "Image par défaut"
                            : imageHasError
                              ? "Fallback actif"
                              : "Image personnalisée"}
                        </p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Contenu */}
                <div className="rounded-xl border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40 sm:p-5">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Contenu
                  </label>

                  <div
                    className="mt-1
                    [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                    [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                    [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                    [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                  >
                    <ReactQuill
                      value={settings.infoclub_content || ""}
                      onChange={(v) =>
                        handleContentChange("infoclub_content", v)
                      }
                      modules={modules}
                      formats={formats}
                      placeholder="<p>Votre message important ici…</p>"
                    />
                  </div>

                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Astuce : utilisez le gras pour les éléments importants.
                  </p>
                </div>

                {/* Actions bas */}
                <div className="flex justify-end">
                  <Button
                    text={saving ? "Enregistrement..." : "Mettre à jour"}
                    type="submit"
                    className="!rounded-xl gap-2"
                    icon={FaSave}
                  />
                </div>
              </form>

              {/* Prévisualisation temps réel */}
              <div className="space-y-4">
                {/* Toggle Dark/Light */}
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setPreviewDark((d) => !d)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white/70 px-3 py-2 text-sm hover:bg-white/90 dark:border-slate-700 dark:bg-slate-900/70 dark:hover:bg-slate-800"
                  >
                    {previewDark ? <FaSun /> : <FaMoon />}
                    {previewDark ? "Clair" : "Sombre"}
                  </button>
                </div>

                <div className={`${previewDark ? "dark" : ""}`}>
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/70 p-5 dark:border-slate-800 dark:bg-slate-900/40">
                    {!previewSettings.isActive ? (
                      <div className="text-sm italic text-slate-600 dark:text-slate-400">
                        La section Info Club est <b>désactivée</b>. Elle
                        n’apparaîtra pas sur l’accueil.
                      </div>
                    ) : (
                      <section className="mx-auto w-full max-w-[430px] px-2 py-4 text-black dark:text-white">
                        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
                          {/* Image en haut - format mobile */}
                          <button
                            type="button"
                            onClick={() => setIsImageOpen(true)}
                            className="group relative block w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-950"
                            title="Agrandir l’image"
                          >
                            <div className="aspect-[16/10] w-full">
                              <img
                                src={previewSettings.image}
                                alt={previewSettings.title || "Info Club"}
                                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                onError={handleImageError}
                              />
                            </div>

                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />

                            <div className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-xl bg-slate-950/70 px-3 py-2 text-xs font-bold text-white opacity-90 shadow-lg backdrop-blur transition group-hover:bg-violet-600 group-hover:opacity-100">
                              <FaExpandAlt />
                              Agrandir
                            </div>
                          </button>

                          {/* Texte en dessous */}
                          <div className="bg-gradient-to-b from-slate-50 to-white p-6 dark:from-slate-800 dark:to-slate-900 sm:p-7">
                            <h2 className="mb-4 text-2xl font-bold leading-tight text-slate-900 dark:text-white sm:text-3xl">
                              {previewSettings.title}
                            </h2>

                            <div
                              className="infoclub-content text-left text-[15px] leading-7 text-slate-700 dark:text-slate-300"
                              dangerouslySetInnerHTML={{
                                __html: previewSettings.content,
                              }}
                            />
                          </div>
                        </div>

                        <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
                          Aperçu mobile : image en haut, contenu en dessous.
                        </p>
                      </section>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Styles locaux */}
        <style>{`
          input::placeholder { color: #94a3b8; }
          .dark input::placeholder { color: #94a3b8; opacity: .7; }

          .dark .infoclub-content strong,
          .dark .infoclub-content b { color: #fff; }

          .infoclub-content ul {
            list-style: disc;
            padding-left: 1.25rem;
          }

          .infoclub-content ol {
            list-style: decimal;
            padding-left: 1.25rem;
          }

          .infoclub-content a {
            color: #2563eb;
            text-decoration: underline;
            text-underline-offset: 3px;
          }

          .dark .infoclub-content a {
            color: #93c5fd;
          }

          html {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        `}</style>
      </section>

      {isImageOpen ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label="Image Info Club agrandie"
          onClick={() => setIsImageOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsImageOpen(false)}
            className="absolute right-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
            aria-label="Fermer l’image agrandie"
          >
            <FaTimes />
          </button>

          <div
            className="relative max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={imagePreviewUrl}
              alt={previewSettings.title || "Info Club"}
              className="max-h-[90vh] w-full object-contain"
              onError={handleImageError}
            />

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950/80 to-transparent p-5">
              <p className="text-sm font-bold text-white">
                {previewSettings.title}
              </p>

              <p className="mt-1 text-xs text-white/70">
                Cliquez en dehors de l’image ou appuyez sur Échap pour fermer.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default InfoClubForm;
